import React, { useState, useEffect, useCallback } from 'react';
import { Users, Phone, Mail, Calendar, Search, RotateCcw } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OldTenants = () => {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('exited'); // exited, notice, all

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await axios.get(`${API}/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const data = filter === 'all'
        ? res.data.filter(t => t.status !== 'active')
        : res.data;
      setTenants(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load archive', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const handleReactivate = async (tenant) => {
    try {
      await axios.put(`${API}/tenants/${tenant.id}`, { ...tenant, status: 'active' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Tenant Reactivated', description: `${tenant.name} is now active again` });
      fetchTenants();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const filtered = tenants.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.name?.toLowerCase().includes(s) || t.phone?.includes(s) || t.email?.toLowerCase().includes(s);
  });

  const stats = {
    total: tenants.length,
    exited: tenants.filter(t => t.status === 'exited').length,
    notice: tenants.filter(t => t.status === 'notice').length,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="old-tenants-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Users /> Old Tenants Archive</h1>
        <p className="text-gray-600 mt-1">Past tenants — exited or on notice period</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border-l-4 border-l-gray-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
          <p className="text-xs text-gray-500">Total Archived</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </Card>
        <Card className="p-5 border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('exited')} data-testid="filter-exited">
          <p className="text-xs text-gray-500">Exited</p>
          <p className="text-3xl font-bold text-red-700 mt-1">{stats.exited}</p>
        </Card>
        <Card className="p-5 border-l-4 border-l-yellow-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('notice')} data-testid="filter-notice">
          <p className="text-xs text-gray-500">On Notice</p>
          <p className="text-3xl font-bold text-yellow-700 mt-1">{stats.notice}</p>
        </Card>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="old-tenants-search"
            type="text"
            placeholder="Search by name, phone, email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg"
          data-testid="old-tenants-filter"
        >
          <option value="exited">Exited Only</option>
          <option value="notice">On Notice</option>
          <option value="all">All Archived</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Users size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No archived tenants</h3>
          <p className="text-gray-600">Exited tenants will appear here</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <Card key={t.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`old-tenant-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center text-white font-bold text-lg">
                    {t.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.check_in_date} → {t.check_out_date || 'Active'}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  t.status === 'exited' ? 'bg-red-100 text-red-800' :
                  t.status === 'notice' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>{t.status}</span>
              </div>
              <div className="space-y-1 text-sm text-gray-600 mb-3">
                <p className="flex items-center gap-2"><Phone size={14} /> {t.phone}</p>
                {t.email && <p className="flex items-center gap-2"><Mail size={14} /> {t.email}</p>}
                <p className="flex items-center gap-2"><Calendar size={14} /> Rent: ₹{(t.rent_amount || 0).toLocaleString()}</p>
              </div>
              <Button
                data-testid={`reactivate-tenant-${t.id}`}
                size="sm"
                variant="outline"
                onClick={() => handleReactivate(t)}
                className="w-full"
              >
                <RotateCcw size={14} className="mr-2" /> Reactivate
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default OldTenants;
